import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, output, signal, untracked, viewChild } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import {
  HdChannelGuessingGameService,
  HdChannelGuessingHistoryEvent,
  HdChannelGuessingMemoryField,
  HdChannelGuessingScoreLevel,
} from '../hd-channel-guessing-game.service';
import { HdChannelTranslation, HdGateInfoService } from '../../hd-gate-info.service';
import { getHdBodygraphChannel } from '../../hd-bodygraph-metadata';
import { HdBodygraphCenter } from '../../hd-bodygraph.models';
import { HdGameFeedbackAudioService } from '../../hd-game-feedback-audio.service';

interface HdChannelGuessingHistoryRow {
  readonly key: string;
  readonly kind: HdChannelGuessingHistoryEvent['kind'];
  readonly label: string;
  readonly icon: string;
  readonly iconClass: string;
  readonly pointsLabel: string;
  readonly pointsClass: string;
  /** Kanaal-key voor rijen die aan een specifiek kanaal hangen, anders `null`. */
  readonly channelKey: string | null;
}

interface HdChannelGuessingHintRow {
  readonly kind: 'mainCircuit' | 'centers';
  readonly categoryLabel: string;
  readonly value: string;
}

const SCORE_LEVEL_TITLE_KEYS: Readonly<Record<HdChannelGuessingScoreLevel, string>> = {
  1: 'humanDesign.games.channelGuessing.scoreLevel1',
  2: 'humanDesign.games.channelGuessing.scoreLevel2',
  3: 'humanDesign.games.channelGuessing.scoreLevel3',
  4: 'humanDesign.games.channelGuessing.scoreLevel4',
  5: 'humanDesign.games.channelGuessing.scoreLevel5',
};

@Component({
  selector: 'hd-channel-guessing-game-panel',
  imports: [SharedModule],
  templateUrl: './hd-channel-guessing-game-panel.component.html',
  styleUrl: './hd-channel-guessing-game-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChannelGuessingGamePanelComponent {
  readonly restartRequested = output<void>();

  protected readonly game = inject(HdChannelGuessingGameService);
  private readonly confirmer = inject(CONFIRMER);
  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly feedbackAudio = inject(HdGameFeedbackAudioService);
  private readonly transloco = inject(TranslocoService);

  /**
   * Vertaalt een sleutel in de **bevroren inhoudstaal** van het lopende spel
   * (`game.currentLang()`, afgeleid van de content-taal-override uit het
   * controls-paneel of de app-taal). Zo schakelt het hele paneel mee wanneer
   * de speler het spel in een andere taal start.
   */
  protected t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(key, params, this.game.currentLang());
  }

  protected readonly audioEnabled = this.feedbackAudio.enabled;

  protected toggleAudio(): void {
    this.feedbackAudio.toggleEnabled();
  }

  protected readonly currentChannelKey = this.game.currentChannelKey;
  protected readonly currentQuestionMode = this.game.currentQuestionMode;
  protected readonly selectedGate = this.game.selectedGate;
  protected readonly canRequestHint = this.game.canRequestHint;
  protected readonly canSkip = this.game.canSkip;
  protected readonly isFinished = this.game.isFinished;
  protected readonly isPerfectGame = this.game.isPerfectGame;
  protected readonly scoreLevel = this.game.scoreLevel;
  protected readonly maxPossibleScore = this.game.maxPossibleScore;
  protected readonly isMemoryMode = this.game.isMemoryMode;
  protected readonly memoryValidations = this.game.currentMemoryValidations;

  /**
   * Per-veld vlag of het paneel nu een aparte 'Valideer'-knop mag tonen. Volgt
   * `canValidateMemoryField` op de service: zodra één veld gevalideerd is,
   * verdwijnen beide knoppen en moet de speler submitten.
   */
  protected readonly canValidateMemoryName = computed(
    () => this.game.canValidateMemoryField('name'),
  );
  protected readonly canValidateMemoryDescription = computed(
    () => this.game.canValidateMemoryField('description'),
  );

  /**
   * Memory-modus invoervelden. We bewaren ze als signals zodat het paneel
   * de error-stijl per veld kan resetten zodra de speler begint te typen,
   * en zodat de autocomplete-suggestie reactief volgt op de omschrijving.
   */
  protected readonly memoryNameInput = signal('');
  protected readonly memoryDescriptionInput = signal('');
  protected readonly memoryNameWrong = signal(false);
  protected readonly memoryDescriptionWrong = signal(false);

  protected readonly memoryNameAutocompleteSuggestion = computed<string | null>(() => {
    if (!this.isMemoryMode() || this.memoryValidations().name) {
      return null;
    }
    return this.game.getMemoryAutocompleteSuggestion('name', this.memoryNameInput());
  });

  protected readonly memoryDescriptionAutocompleteSuggestion = computed<string | null>(() => {
    if (!this.isMemoryMode() || this.memoryValidations().description) {
      return null;
    }
    return this.game.getMemoryAutocompleteSuggestion('description', this.memoryDescriptionInput());
  });

  /**
   * Referentie naar het eerste invulveld zodat we de focus terug kunnen
   * zetten zodra een nieuw kanaal aan de beurt komt (start, skip, of correct
   * antwoord). Dit laat de speler volledig met het toetsenbord doorgaan.
   */
  private readonly memoryNameInputRef = viewChild<ElementRef<HTMLInputElement>>('memoryNameInputRef');

  /**
   * Reset de invoer en error-vlaggen telkens het huidige kanaal verandert
   * (start, skip, of correct antwoord). Voorkomt dat een vorige fout-stijl
   * blijft hangen bij het volgende kanaal. Geeft daarna de focus terug aan
   * het eerste invulveld zodat de speler zonder muis kan doorspelen.
   */
  private readonly _resetMemoryInputsOnChannelChangeEffect = effect(() => {
    this.currentChannelKey();
    untracked(() => {
      this.clearMemoryInputs();
      if (this.isMemoryMode() && !this.isFinished()) {
        setTimeout(() => this.memoryNameInputRef()?.nativeElement?.focus(), 0);
      }
    });
  });

  /** Poorten van het huidige kanaal, oplopend gesorteerd voor consistente weergave. */
  protected readonly currentChannelSortedGates = computed<readonly [number, number] | null>(() => {
    const gates = this.game.currentChannelGates();
    if (gates == null) {
      return null;
    }
    const [a, b] = gates;
    return a <= b ? [a, b] : [b, a];
  });

  protected readonly currentChannelTranslation = computed<HdChannelTranslation | null>(() => {
    const key = this.currentChannelKey();
    return key != null ? this.gateInfoService.channelTranslationIn(key, this.game.currentLang()) : null;
  });

  protected readonly wrongAttemptIndices = computed(() =>
    indicesArray(this.game.wrongAttemptsForCurrentChannel()),
  );

  protected readonly hintRows = computed<readonly HdChannelGuessingHintRow[]>(() => {
    const key = this.currentChannelKey();
    const used = this.game.hintsUsedForCurrentChannel();
    if (key == null || used === 0) {
      return [];
    }
    return buildHintRows(key, used, this.gateInfoService, this.game.currentLang()).slice(0, used);
  });

  protected readonly historyRows = computed<readonly HdChannelGuessingHistoryRow[]>(() => {
    const events = this.game.events();
    const lang = this.game.currentLang();
    return events.map((event, index) =>
      buildHistoryRow(event, index, this.game, this.gateInfoService, lang, (key, params) => this.t(key, params)),
    );
  });

  /**
   * Globale toggle die bepaalt of de volledige hint-blokken standaard zichtbaar
   * zijn onder elke correct geraden kanaal-rij in de history. Per-rij overrides
   * (zie `_historyExpansionOverrides`) gaan voor op deze default.
   */
  protected readonly showAllHintsInHistoryByDefault = signal(false);

  /**
   * Expliciete override per history-rij-key: `true` = uitgeklapt, `false` =
   * dichtgeklapt. Ontbrekende keys volgen de globale default. Wordt gevuld door
   * een klik op een correcte history-entry en weer opgeruimd zodra de keuze
   * samenvalt met de huidige default.
   */
  private readonly _historyExpansionOverrides = signal<ReadonlyMap<string, boolean>>(new Map());

  /**
   * Hint-rijen per kanaal voor weergave in de history. Bevat per kanaal de
   * volledige set hint-rijen (hoofdcircuit + centers) zodat de speler na een
   * correct antwoord de bijhorende info kan opvragen — onafhankelijk van of
   * er tijdens de vraag effectief hints werden gebruikt.
   */
  protected readonly historyHintRowsByChannel = computed<ReadonlyMap<string, readonly HdChannelGuessingHintRow[]>>(() => {
    const events = this.game.events();
    const keys = new Set<string>();
    for (const event of events) {
      const key = channelKeyOfEvent(event);
      if (key != null) {
        keys.add(key);
      }
    }

    const lang = this.game.currentLang();
    const result = new Map<string, readonly HdChannelGuessingHintRow[]>();
    for (const key of keys) {
      result.set(key, buildHintRows(key, HdChannelGuessingGameService.maxHintsPerChannel, this.gateInfoService, lang));
    }
    return result;
  });

  /**
   * Reset de per-rij overrides zodra de eventslijst leegloopt (typisch bij
   * spelherstart). Voorkomt dat verouderde keys blijven hangen wanneer in een
   * volgende ronde toevallig dezelfde key zou ontstaan.
   */
  private readonly _resetHistoryExpansionOnRestartEffect = effect(() => {
    if (this.game.events().length === 0) {
      untracked(() => this._historyExpansionOverrides.set(new Map()));
    }
  });

  protected readonly scoreLabel = computed(() => formatScore(this.game.score()));

  protected readonly finishedTitle = computed(() => {
    const level = this.scoreLevel();
    return this.t(level ? SCORE_LEVEL_TITLE_KEYS[level] : 'humanDesign.games.channelGuessing.finishedFallbackTitle');
  });

  protected readonly starIndices = computed(() => indicesArray(this.scoreLevel() ?? 0));
  protected readonly emptyStarIndices = computed(() => indicesArray(5 - (this.scoreLevel() ?? 0)));

  protected requestHint(): void {
    if (this.isMemoryMode()) {
      this.requestMemoryHint();
      return;
    }
    this.game.requestHint();
  }

  /**
   * Memory-modus hint: vraagt de service om enkele letters te onthullen,
   * vervangt de beide invoer-signals door de teruggegeven waarden en wist
   * eventuele rode error-states (de onthulde tekst bevat de canonieke prefix
   * en is dus per definitie 'in orde').
   */
  private requestMemoryHint(): void {
    const reveal = this.game.requestMemoryHint(this.memoryNameInput(), this.memoryDescriptionInput());
    if (reveal == null) {
      return;
    }
    this.memoryNameInput.set(reveal.name);
    this.memoryDescriptionInput.set(reveal.description);
    this.memoryNameWrong.set(false);
    this.memoryDescriptionWrong.set(false);
  }

  protected requestSkip(): void {
    this.game.skip();
  }

  protected onMemoryNameInput(value: string): void {
    this.memoryNameInput.set(value);
    if (this.memoryNameWrong()) {
      this.memoryNameWrong.set(false);
    }
  }

  protected onMemoryDescriptionInput(value: string): void {
    this.memoryDescriptionInput.set(value);
    if (this.memoryDescriptionWrong()) {
      this.memoryDescriptionWrong.set(false);
    }
  }

  protected applyMemoryNameAutocomplete(): void {
    const suggestion = this.memoryNameAutocompleteSuggestion();
    if (suggestion != null) {
      this.memoryNameInput.set(suggestion);
      this.memoryNameWrong.set(false);
    }
  }

  protected applyMemoryDescriptionAutocomplete(): void {
    const suggestion = this.memoryDescriptionAutocompleteSuggestion();
    if (suggestion != null) {
      this.memoryDescriptionInput.set(suggestion);
      this.memoryDescriptionWrong.set(false);
    }
  }

  /**
   * Laat de speler een actieve autocomplete-suggestie bevestigen via Tab.
   * Zonder suggestie behoudt Tab zijn standaardgedrag (focus opschuiven naar
   * het volgende veld in de tab-volgorde). Shift+Tab blijft altijd intact om
   * achterwaarts te kunnen navigeren.
   */
  protected onMemoryNameKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || event.shiftKey) {
      return;
    }
    const suggestion = this.memoryNameAutocompleteSuggestion();
    if (suggestion != null && suggestion !== this.memoryNameInput()) {
      event.preventDefault();
      this.applyMemoryNameAutocomplete();
    }
  }

  protected onMemoryDescriptionKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || event.shiftKey) {
      return;
    }
    const suggestion = this.memoryDescriptionAutocompleteSuggestion();
    if (suggestion != null && suggestion !== this.memoryDescriptionInput()) {
      event.preventDefault();
      this.applyMemoryDescriptionAutocomplete();
    }
  }

  /**
   * Valideert losstaand één veld zonder score-impact. Bij succes vervangen we
   * de invoer door de canonieke spelling (zodat fuzzy-correcties zichtbaar
   * worden), wissen de error-state en verstoppen we beide validatie-knoppen
   * (de service zorgt dat `canValidateMemoryField` voor het andere veld nu
   * `false` teruggeeft). Bij fout antwoord kleurt het veld rood tot de speler
   * verder typt.
   */
  protected validateMemoryField(field: HdChannelGuessingMemoryField): void {
    const typed = field === 'name' ? this.memoryNameInput() : this.memoryDescriptionInput();
    const result = this.game.validateMemoryField(field, typed);
    if (result.kind === 'correct') {
      if (field === 'name') {
        this.memoryNameInput.set(result.canonical);
        this.memoryNameWrong.set(false);
      } else {
        this.memoryDescriptionInput.set(result.canonical);
        this.memoryDescriptionWrong.set(false);
      }
      return;
    }
    if (result.kind === 'wrong') {
      if (field === 'name') {
        this.memoryNameWrong.set(true);
      } else {
        this.memoryDescriptionWrong.set(true);
      }
    }
  }

  protected submitMemoryAnswer(): void {
    const result = this.game.submitMemoryAnswer(this.memoryNameInput(), this.memoryDescriptionInput());
    if (result.kind === 'wrong') {
      this.memoryNameWrong.set(result.nameWrong);
      this.memoryDescriptionWrong.set(result.descriptionWrong);
      this.feedbackAudio.playWrong();
      return;
    }
    if (result.kind === 'correct') {
      this.clearMemoryInputs();
      this.feedbackAudio.playCorrect();
    }
  }

  private clearMemoryInputs(): void {
    this.memoryNameInput.set('');
    this.memoryDescriptionInput.set('');
    this.memoryNameWrong.set(false);
    this.memoryDescriptionWrong.set(false);
  }

  protected toggleShowAllHintsInHistoryByDefault(): void {
    this.showAllHintsInHistoryByDefault.update(v => !v);
  }

  /**
   * Een rij is uitklapbaar zodra ze aan een correct geraden kanaal hangt en
   * de hint-rijen voor dat kanaal beschikbaar zijn. We tonen het hintblok
   * enkel onder correct geraden kanalen — andere event-soorten (wrong, hint,
   * skip, bonus) blijven compact.
   */
  protected isHistoryRowExpandable(row: HdChannelGuessingHistoryRow): boolean {
    return row.kind === 'correct'
      && row.channelKey != null
      && (this.historyHintRowsByChannel().get(row.channelKey)?.length ?? 0) > 0;
  }

  protected isHistoryRowExpanded(row: HdChannelGuessingHistoryRow): boolean {
    if (!this.isHistoryRowExpandable(row)) {
      return false;
    }
    const override = this._historyExpansionOverrides().get(row.key);
    return override ?? this.showAllHintsInHistoryByDefault();
  }

  protected hintRowsForHistoryRow(row: HdChannelGuessingHistoryRow): readonly HdChannelGuessingHintRow[] {
    return row.channelKey != null ? (this.historyHintRowsByChannel().get(row.channelKey) ?? []) : [];
  }

  protected toggleHistoryRowExpansion(row: HdChannelGuessingHistoryRow): void {
    if (!this.isHistoryRowExpandable(row)) {
      return;
    }
    const desired = !this.isHistoryRowExpanded(row);
    const next = new Map(this._historyExpansionOverrides());
    if (desired === this.showAllHintsInHistoryByDefault()) {
      next.delete(row.key);
    } else {
      next.set(row.key, desired);
    }
    this._historyExpansionOverrides.set(next);
  }

  protected onHistoryRowKeydown(event: KeyboardEvent, row: HdChannelGuessingHistoryRow): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.toggleHistoryRowExpansion(row);
  }

  protected async requestRestart(): Promise<void> {
    if (!this.game.isActive() || this.isFinished()) {
      this.restartRequested.emit();
      return;
    }

    const confirmed = await this.confirmer.confirm(
      this.t('humanDesign.games.channelGuessing.restartConfirm'),
      { color: 'danger' },
    );

    if (confirmed) {
      this.restartRequested.emit();
    }
  }
}

function indicesArray(count: number): readonly number[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => i);
}

function formatScore(score: number): string {
  return Number.isInteger(score) ? `${score}` : score.toFixed(1);
}

function formatPoints(points: number): string {
  if (points > 0) return `+${formatScore(points)}`;
  if (points < 0) return formatScore(points);
  return '0';
}

function formatChannelGatePair(channelKey: string): string {
  const channel = getHdBodygraphChannel(channelKey);
  if (channel == null) {
    return channelKey;
  }
  const [a, b] = channel.gates;
  const [min, max] = a <= b ? [a, b] : [b, a];
  return `${min} – ${max}`;
}

/**
 * Bouwt de hint-rijen voor het actieve kanaal in de vaste volgorde
 * `mainCircuit` → `centers`. Slots zonder beschikbare data worden gefilterd
 * (bv. een kanaal zonder bekend hoofdcircuit). Per spec maximaal 2 hints.
 */
function buildHintRows(
  channelKey: string,
  hintsUsed: number,
  gateInfoService: HdGateInfoService,
  lang: string,
): readonly HdChannelGuessingHintRow[] {
  const channel = getHdBodygraphChannel(channelKey);
  if (channel == null) {
    return [];
  }

  const rows: HdChannelGuessingHintRow[] = [];
  const mainCircuitName = resolveMainCircuitName(channelKey, gateInfoService, lang);
  if (mainCircuitName != null) {
    rows.push({ kind: 'mainCircuit', categoryLabel: 'humanDesign.games.channelGuessing.categoryMainCircuit', value: mainCircuitName });
  }
  rows.push({
    kind: 'centers',
    categoryLabel: 'humanDesign.games.channelGuessing.categoryConnects',
    value: formatCenters(channel.centers, gateInfoService, lang),
  });

  return rows.slice(0, hintsUsed);
}

function resolveMainCircuitName(
  channelKey: string,
  gateInfoService: HdGateInfoService,
  lang: string,
): string | null {
  const circuit = gateInfoService.channelMainCircuit(channelKey);
  if (circuit == null) {
    return null;
  }
  return gateInfoService.circuitNameIn(circuit.key, lang);
}

function formatCenters(
  centers: readonly [HdBodygraphCenter, HdBodygraphCenter],
  gateInfoService: HdGateInfoService,
  lang: string,
): string {
  const labels = centers.map(c => gateInfoService.centerLabelIn(c, lang));
  return `${labels[0]} ↔ ${labels[1]}`;
}

/**
 * Bouwt één geschiedenis-rij. Voor `wrong`/`hint`/`skip` tonen we enkel de
 * informatie die de speler op dat moment al zag (kanaal-nummer in `numbers`-
 * modus, keynote-naam in `keynotes`-modus) zodat het antwoord niet wordt
 * weggegeven. Voor `correct` tonen we altijd de volledige info (`min – max +
 * naam`). `streak-bonus` en `perfect-bonus` hebben geen kanaal-context.
 */
function buildHistoryRow(
  event: HdChannelGuessingHistoryEvent,
  index: number,
  game: HdChannelGuessingGameService,
  gateInfoService: HdGateInfoService,
  lang: string,
  translate: (key: string, params?: Record<string, unknown>) => string,
): HdChannelGuessingHistoryRow {
  const pointsLabel = formatPoints(event.points);
  const pointsClass = event.points > 0 ? 'is-positive' : event.points < 0 ? 'is-negative' : 'is-neutral';

  switch (event.kind) {
    case 'correct': {
      const translation = gateInfoService.channelTranslationIn(event.channelKey, lang);
      const gatePair = formatChannelGatePair(event.channelKey);
      const label = formatCorrectHistoryLabel(gatePair, translation);
      return { key: `correct-${event.channelKey}-${index}`, kind: 'correct', label, icon: 'check', iconClass: 'is-success', pointsLabel, pointsClass, channelKey: event.channelKey };
    }
    case 'wrong':
      return { key: `wrong-${event.channelKey}-${index}`, kind: 'wrong', label: formatHistoryQuestionLabel(event.channelKey, game, gateInfoService, lang), icon: 'times', iconClass: 'is-danger', pointsLabel, pointsClass, channelKey: event.channelKey };
    case 'hint':
      return { key: `hint-${event.channelKey}-${index}`, kind: 'hint', label: formatHistoryQuestionLabel(event.channelKey, game, gateInfoService, lang), icon: 'lightbulb', iconClass: 'is-warning', pointsLabel, pointsClass, channelKey: event.channelKey };
    case 'skip':
      return { key: `skip-${event.channelKey}-${index}`, kind: 'skip', label: formatHistoryQuestionLabel(event.channelKey, game, gateInfoService, lang), icon: 'forward', iconClass: 'is-muted', pointsLabel, pointsClass, channelKey: event.channelKey };
    case 'streak-bonus':
      return { key: `streak-${event.streak}-${index}`, kind: 'streak-bonus', label: translate('humanDesign.games.channelGuessing.historyStreak', { streak: event.streak }), icon: 'fire', iconClass: 'is-streak', pointsLabel, pointsClass, channelKey: null };
    case 'perfect-bonus':
      return { key: `perfect-${index}`, kind: 'perfect-bonus', label: translate('humanDesign.games.channelGuessing.historyPerfect'), icon: 'trophy', iconClass: 'is-perfect', pointsLabel, pointsClass, channelKey: null };
  }
}

function formatHistoryQuestionLabel(
  channelKey: string,
  game: HdChannelGuessingGameService,
  gateInfoService: HdGateInfoService,
  lang: string,
): string {
  const questionMode = game.questionModeForChannel(channelKey);
  if (questionMode === 'keynotes') {
    const translation = gateInfoService.channelTranslationIn(channelKey, lang);
    return translation?.name ?? formatChannelGatePair(channelKey);
  }
  return formatChannelGatePair(channelKey);
}

/**
 * Bouwt het label voor een correct geraden kanaal in de geschiedenis. Zonder
 * vertaling tonen we enkel het poort-paar; met vertaling de naam en — indien
 * beschikbaar — de korte omschrijving (zelfde stijl als de vraag-weergave).
 */
function formatCorrectHistoryLabel(
  gatePair: string,
  translation: HdChannelTranslation | null,
): string {
  if (translation == null) {
    return gatePair;
  }
  if (translation.shortDescription) {
    return `${gatePair} · ${translation.name} – ${translation.shortDescription}`;
  }
  return `${gatePair} · ${translation.name}`;
}

function channelKeyOfEvent(event: HdChannelGuessingHistoryEvent): string | null {
  return event.kind === 'correct' || event.kind === 'wrong' || event.kind === 'hint' || event.kind === 'skip'
    ? event.channelKey
    : null;
}
